<template>
  <div class="grid gap-2.5 px-4 py-2.5">
    <div
      class="flex items-center gap-3 rounded-lg border bg-yapgray-200 p-4 dark:border-yapdarkgray-800 dark:bg-yapdarkgray-950"
    >
      <YapIcon name="info-outline" class="h-7 w-7 text-primary-500 dark:text-primary-dark-500" />
      <div>
        <h4 class="text-xl text-primary-950 dark:text-primary-dark-200">{{ t('app.payments.executionHintTitle') }}</h4>
        <p class="text-sm text-yapgray-600 dark:text-yapgray-300">
          {{ t('app.payments.standing_order_selection.txt') }}
        </p>
      </div>
    </div>
    <div class="rounded-lg border bg-white p-4 dark:border-yapdarkgray-700 dark:bg-yapdarkgray-800">
      <h3 class="text-lg">{{ t('app.payments.beneficiaryCardTitle') }}</h3>
      <LabelValue :label="t('app.payments.beneficiaryNameLabel')" :value="paymentForm.beneficiary.name" />
      <LabelValue
        :label="t('app.payments.ibanNumberLabel')"
        :value="formatIban(paymentForm.beneficiary.iban)"
        :monospaced-value="true"
      />
      <LabelValue
        :label="t('app.payments.bic.swift')"
        :value="paymentForm.beneficiary.swiftBIC"
        :monospaced-value="true"
      />
    </div>
    <div
      class="rounded-lg border bg-white p-4 dark:border-yapdarkgray-700 dark:bg-yapdarkgray-800"
      v-if="paymentForm.beneficiary.address && displayAddressFields"
    >
      <h3 class="text-lg">{{ t('app.payments.beneficiaryAddressCardTitle') }}</h3>
      <LabelValue :label="t('dict.country')" :value="state.countryName" />
      <div class="grid grid-cols-2">
        <LabelValue :label="t('dict.town')" :value="paymentForm.beneficiary.address.city" />
        <LabelValue :label="t('dict.zipCode')" :value="paymentForm.beneficiary.address.postalCode" />
        <LabelValue :label="t('dict.street')" :value="paymentForm.beneficiary.address.street" />
        <LabelValue :label="t('dict.houseNo')" :value="paymentForm.beneficiary.address.number ?? '-'" />
      </div>
    </div>
    <div class="rounded-lg border bg-white p-4 dark:border-yapdarkgray-700 dark:bg-yapdarkgray-800">
      <h3 class="text-lg">{{ t('app.payments.amountCardTitle') }}</h3>
      <div class="grid grid-cols-2">
        <LabelValue :label="t('dict.amount')" :value="Number.parseFloat(paymentForm.amount).toFixed(2)" />
        <LabelValue :label="t('dict.currency')" :value="paymentForm.currency" />
        <template v-if="!!incoreQuote && isIncorePayment(paymentForm)">
          <LabelValue :label="t('webapp.payments.incore.fee.text')">
            <YapAmount :yap-money="incoreQuote.additionalFee" />
          </LabelValue>
          <LabelValue :label="t('app.payments.incore.fxRate.label')">
            <YapAmount :yap-amount="incoreQuote.exchangeRate" />
          </LabelValue>
          <LabelValue :label="t('app.payments.incore.totalAmount.text')">
            <div class="flex items-center gap-2">
              <YapAmount :yap-money="incoreQuote.sourceAmount" />
              <YapTooltip
                :text="t('app.payments.incore.fee.disclaimer')"
                class="xml-2 min-w-64 -translate-y-4 translate-x-1/2"
              >
                <YapIcon name="info-outline" class="size-4 text-yapgray-500 dark:text-yapgray-500" />
              </YapTooltip>
            </div>
          </LabelValue>
        </template>
      </div>
      <div class="grid grid-cols-2">
        <LabelValue :label="t('app.payments.paymentDate')" :value="formatIsoToLocaleString(paymentForm.paymentTime)" />
        <LabelValue :label="t('app.payments.standing_order.txt')" :value="paymentForm.recurrence" />
      </div>
    </div>
    <div class="rounded-lg border bg-white p-4 dark:border-yapdarkgray-700 dark:bg-yapdarkgray-800">
      <h3 class="text-lg">{{ t('app.payments.detailsCardTitle') }}</h3>
      <LabelValue
        :label="t('app.payments.referenceNumberLabel')"
        :value="paymentForm.structuredReference ?? '-'"
        :monospaced-value="true"
      />
      <LabelValue :label="t('app.payments.messageToBeneficiary')" :value="paymentForm.paymentReason ?? '-'" />
      <LabelValue :label="t('dict.category')" :value="t(`transaction-categories.${paymentForm.category}`)" />

      <LabelValue :label="t('app.payments.personalNoteLabel')" :value="paymentForm.noteText ?? '-'" />
      <LabelValue :label="t('webapp.payments.attachments.label')">
        <span v-if="!fileAttachments?.length">-</span>
        <ul
          v-else
          class="mt-2 divide-y overflow-hidden rounded-lg border dark:divide-yapdarkgray-500 dark:border-yapdarkgray-600"
        >
          <li
            v-for="fileResource in fileAttachments"
            :key="fileResource.bucketFileName"
            class="flex items-center justify-between p-2"
          >
            {{ fileResource.displayName }}
          </li>
        </ul>
      </LabelValue>
    </div>
    <YapAlert
      v-if="errors"
      :message="tOptions(`app.payments.${errors.code}`, { fallback: t('app.payments.unknown_error') })"
      :type="AlertTypes.Error"
      data-testid="error-message"
    />
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted } from 'vue';

import { useI18n } from '@/plugins/i18n';
import { formatIban } from '@/utils/formats';
import YapIcon from '@/components/YapIcon.vue';
import { CashAccount } from '@/types/cashAccount';
import YapAmount from '@/components/YapAmount.vue';
import { FileResource } from '@/types/fileResource';
import LabelValue from '@/components/LabelValue.vue';
import YapTooltip from '@/components/YapTooltip.vue';
import { useRefdataStore } from '@/store/refdataStore';
import { formatIsoToLocaleString } from '@/utils/locale';
import usePaymentForm from '@/composables/usePaymentForm';
import { useCashAccountsStore } from '@/store/cashAccountsStore';
import YapAlert, { AlertTypes } from '@/components/YapAlert.vue';
import { PaymentFormData } from '@/components/payments/types/payment';
import { IncoreQuote } from '@/components/payments/types/incoreQuote';

const props = defineProps<{
  paymentForm: PaymentFormData;
  displayAddressFields: boolean;
  incoreQuote?: IncoreQuote;
  debitCashAccountUid: string;
  fileAttachments?: Array<FileResource>;
  errors?: { code: string };
}>();

const { t, tOptions } = useI18n();
const refdataStore = useRefdataStore();
const { isIncorePayment } = usePaymentForm();
const cashAccountsStore = useCashAccountsStore();

interface State {
  selectedCashAccount?: CashAccount;
  countryName: string;
}

const state: State = reactive({
  selectedCashAccount: computed(() => {
    return cashAccountsStore.getCashAccount(props.debitCashAccountUid);
  }),
  countryName: ''
});

onMounted(async () => {
  if (!!props.paymentForm.beneficiary.address?.country) {
    state.countryName = await refdataStore.countryCodeToName(props.paymentForm.beneficiary.address.country);
  }
});
</script>
